import React, { useState, useEffect } from "react";
import { 
  X, ArrowRight, ArrowLeft, Target, TrendingUp, Clock, Users, 
  Award, Zap, Settings, Activity, User, MapPin, Sparkles, Check
} from "lucide-react";
import { getPersonaFromScore } from "../utils/golfPersona";
import { IRON_SETS } from "../utils/clubSets";
import { HeightPicker, BirthdatePicker } from "./ui/WheelPicker";

interface OnboardingWizardProps {
  isOpen: boolean;
  onComplete: (data: OnboardingData) => void;
  onSkip: () => void;
  onClose: () => void;
}

export interface OnboardingData {
  playingExperience: "beginner" | "casual" | "regular" | "serious";
  averageScore?: number;
  courseFrequency: "rarely" | "monthly" | "weekly" | "multiple";
  primaryGoal?: "lower-scores" | "more-distance" | "consistency" | "course-management" | "fun";
  hasExistingClubs: boolean;
  selectedClubs?: string[];
  hasDriver?: string;
  hasWoods?: string[];
  hasHybrids?: string[];
  selectedIronSet?: string;
  selectedWedgeLofts?: number[];
  selectedWedgeSet?: string;
  starterSetId?: string;
  sevenIronDistance?: number;
  ballFlight: "straight" | "draw" | "fade" | "slice" | "hook";
  swingTempo: "slow" | "medium" | "fast";
  biggestChallenge?: "driver" | "approach" | "short-game" | "putting";
  mostCommonMiss?: "right" | "left" | "short" | "long" | "inconsistent";
  heightFeet?: number;
  heightInches?: number;
  weight?: number;
  birthMonth?: number;
  birthDay?: number;
  birthYear?: number;
  age?: number;
  physicalLimitations?: string[];
  mainChallenges: string[];
}

// Premium Option Card Component
const OptionCard: React.FC<{
  label: string;
  description?: string;
  icon?: React.ReactNode;
  selected: boolean;
  isMultiSelect?: boolean;
  onClick: () => void;
  delay?: number;
}> = ({ label, description, icon, selected, isMultiSelect, onClick, delay = 0 }) => (
  <button
    onClick={onClick}
    className={`
      w-full text-left p-4 rounded-2xl border-2 
      transition-all duration-300 ease-out
      transform hover:scale-[1.02] active:scale-[0.98]
      ${selected
        ? "border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 shadow-lg shadow-indigo-500/20"
        : "border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md"
      }
    `}
    style={{ 
      animationDelay: `${delay}ms`,
      animation: 'fade-in-up 0.4s ease-out forwards'
    }}
  >
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {icon && (
          <div className={`
            flex items-center justify-center w-10 h-10 rounded-xl
            transition-all duration-300
            ${selected 
              ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30' 
              : 'bg-gray-100 text-gray-500'
            }
          `}>
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className={`font-semibold text-base ${selected ? "text-indigo-900" : "text-gray-900"}`}>
            {label}
          </div>
          {description && (
            <div className={`text-sm mt-0.5 ${selected ? "text-indigo-600" : "text-gray-500"}`}>
              {description}
            </div>
          )}
        </div>
      </div>
      
      {/* Selection indicator */}
      <div className={`
        flex-shrink-0 w-6 h-6 rounded-${isMultiSelect ? 'lg' : 'full'}
        border-2 flex items-center justify-center
        transition-all duration-300
        ${selected 
          ? 'bg-gradient-to-br from-indigo-500 to-purple-600 border-indigo-500' 
          : 'bg-white border-gray-300'
        }
      `}>
        {selected && (
          <Check size={14} className="text-white" strokeWidth={3} />
        )}
      </div>
    </div>
  </button>
);

// Premium Slider Component
const PremiumSlider: React.FC<{
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  unit?: string;
  labels?: [string, string, string];
  color?: 'green' | 'blue' | 'purple';
}> = ({ value, min, max, onChange, unit, labels, color = 'blue' }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  
  const gradients = {
    green: 'from-emerald-400 to-teal-500',
    blue: 'from-indigo-400 to-purple-500',
    purple: 'from-purple-400 to-pink-500',
  };

  return (
    <div className="space-y-4">
      {/* Value display */}
      <div className="text-center">
        <div className={`text-6xl font-bold bg-gradient-to-r ${gradients[color]} bg-clip-text text-transparent`}>
          {value}
          {unit && <span className="text-3xl ml-1 text-gray-400">{unit}</span>}
        </div>
      </div>
      
      {/* Slider track */}
      <div className="relative pt-2">
        <input
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="
            w-full h-4 rounded-full appearance-none cursor-pointer
            bg-gray-200
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-8
            [&::-webkit-slider-thumb]:h-8
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-gradient-to-br
            [&::-webkit-slider-thumb]:from-indigo-500
            [&::-webkit-slider-thumb]:to-purple-600
            [&::-webkit-slider-thumb]:shadow-xl
            [&::-webkit-slider-thumb]:shadow-indigo-500/40
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-webkit-slider-thumb]:ring-4
            [&::-webkit-slider-thumb]:ring-white
            [&::-webkit-slider-thumb]:transition-all
            [&::-webkit-slider-thumb]:duration-200
            [&::-webkit-slider-thumb]:hover:scale-110
            [&::-moz-range-thumb]:w-8
            [&::-moz-range-thumb]:h-8
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:bg-gradient-to-br
            [&::-moz-range-thumb]:from-indigo-500
            [&::-moz-range-thumb]:to-purple-600
            [&::-moz-range-thumb]:border-4
            [&::-moz-range-thumb]:border-white
            [&::-moz-range-thumb]:shadow-xl
            [&::-moz-range-thumb]:cursor-pointer
          "
        />
        {/* Progress fill */}
        <div 
          className={`absolute top-2 left-0 h-4 rounded-l-full bg-gradient-to-r ${gradients[color]} pointer-events-none`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {/* Labels */}
      {labels && (
        <div className="flex justify-between text-xs text-gray-500 px-1">
          {labels.map((label, i) => (
            <span key={i}>{label}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  isOpen,
  onComplete,
  onSkip,
  onClose,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [data, setData] = useState<OnboardingData>({
    playingExperience: "" as any,
    averageScore: undefined,
    courseFrequency: "" as any,
    primaryGoal: undefined,
    hasExistingClubs: false,
    selectedClubs: [],
    hasDriver: undefined,
    hasWoods: [],
    hasHybrids: [],
    selectedIronSet: undefined,
    selectedWedgeLofts: [],
    selectedWedgeSet: undefined,
    starterSetId: undefined,
    sevenIronDistance: undefined,
    ballFlight: "" as any,
    swingTempo: "" as any,
    biggestChallenge: undefined,
    mostCommonMiss: undefined,
    heightFeet: 5,
    heightInches: 10,
    weight: undefined,
    age: undefined,
    physicalLimitations: [],
    mainChallenges: [],
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const calculateAge = (year: number, month: number, day: number) => {
    const today = new Date();
    let age = today.getFullYear() - year;
    const birthdayThisYear = new Date(today.getFullYear(), month - 1, day);
    if (today < birthdayThisYear) {
      age--;
    }
    return age;
  };

  const steps = [
    // SECTION 1: WHO YOU ARE
    {
      id: "experience",
      section: 1,
      sectionTitle: "WHO YOU ARE",
      title: "How would you describe your golf experience?",
      subtitle: "This helps us tailor recommendations to your level",
      icon: Users,
      options: [
        { value: "beginner", label: "New to Golf", description: "Just starting or < 10 rounds" },
        { value: "casual", label: "Casual Player", description: "Play occasionally, learning basics" },
        { value: "regular", label: "Regular Player", description: "Play regularly, solid fundamentals" },
        { value: "serious", label: "Serious Golfer", description: "Frequent play, always improving" },
      ],
    },
    {
      id: "averageScore",
      section: 1,
      sectionTitle: "WHO YOU ARE",
      title: "What do you typically shoot?",
      subtitle: "This unlocks 10+ personalized metrics about your game!",
      icon: Award,
      isSlider: true,
      sliderMin: 65,
      sliderMax: 150,
      sliderDefault: 95,
      showPersonas: true,
    },
    {
      id: "frequency",
      section: 1,
      sectionTitle: "WHO YOU ARE",
      title: "How often do you play?",
      subtitle: "Helps us understand your commitment level",
      icon: Clock,
      options: [
        { value: "rarely", label: "A few times a year", description: "Special occasions" },
        { value: "monthly", label: "Once a month", description: "Regular hobby" },
        { value: "weekly", label: "Weekly", description: "Dedicated golfer" },
        { value: "multiple", label: "Multiple per week", description: "Golf is life" },
      ],
    },
    {
      id: "primaryGoal",
      section: 1,
      sectionTitle: "WHO YOU ARE",
      title: "What's your #1 goal this season?",
      subtitle: "We'll prioritize recommendations for this",
      icon: Target,
      options: [
        { value: "lower-scores", label: "🎯 Lower Scores", description: "Break 100, 90, or 80" },
        { value: "more-distance", label: "💪 More Distance", description: "Hit it farther" },
        { value: "consistency", label: "🎨 Better Consistency", description: "Fewer bad shots" },
        { value: "course-management", label: "🧠 Smarter Strategy", description: "Better decisions" },
        { value: "fun", label: "😊 Just Have Fun", description: "Enjoy the game" },
      ],
    },
    // SECTION 2: EQUIPMENT
    {
      id: "hasClubs",
      section: 2,
      sectionTitle: "YOUR EQUIPMENT",
      title: "Do you own golf clubs?",
      subtitle: "We'll help you build your perfect bag",
      icon: Settings,
      options: [
        { value: "yes", label: "Yes, I have clubs", description: "I'll select what I own" },
        { value: "no", label: "No, I need clubs", description: "Help me find a starter set" },
      ],
    },
    {
      id: "hasDriver",
      section: 2,
      sectionTitle: "YOUR EQUIPMENT",
      title: "Do you have a driver?",
      subtitle: "The longest club in your bag",
      icon: MapPin,
      options: [
        { value: "yes", label: "Yes", description: "I have a driver" },
        { value: "no", label: "No", description: "Don't carry one" },
      ],
      conditionalShow: (d: OnboardingData) => d.hasExistingClubs === true,
    },
    {
      id: "hasWoods",
      section: 2,
      sectionTitle: "YOUR EQUIPMENT",
      title: "Which fairway woods do you carry?",
      subtitle: "Select all that apply",
      icon: MapPin,
      isMultiSelect: true,
      options: [
        { value: "3w", label: "3-Wood", description: "15°" },
        { value: "5w", label: "5-Wood", description: "18°" },
        { value: "7w", label: "7-Wood", description: "21°" },
        { value: "none", label: "None", description: "No fairway woods" },
      ],
      conditionalShow: (d: OnboardingData) => d.hasExistingClubs === true,
    },
    {
      id: "hasHybrids",
      section: 2,
      sectionTitle: "YOUR EQUIPMENT",
      title: "Which hybrids do you carry?",
      subtitle: "Select all that apply",
      icon: MapPin,
      isMultiSelect: true,
      options: [
        { value: "3h", label: "3-Hybrid", description: "19-21°" },
        { value: "4h", label: "4-Hybrid", description: "22-24°" },
        { value: "5h", label: "5-Hybrid", description: "25-27°" },
        { value: "none", label: "None", description: "No hybrids" },
      ],
      conditionalShow: (d: OnboardingData) => d.hasExistingClubs === true,
    },
    {
      id: "selectedIronSet",
      section: 2,
      sectionTitle: "YOUR EQUIPMENT",
      title: "Which iron set do you carry?",
      subtitle: "Most golfers play with a matching set",
      icon: MapPin,
      options: IRON_SETS.map(set => ({
        value: set.id,
        label: set.name,
        description: set.description
      })),
      conditionalShow: (d: OnboardingData) => d.hasExistingClubs === true,
    },
    {
      id: "selectedWedgeLofts",
      section: 2,
      sectionTitle: "YOUR EQUIPMENT",
      title: "Additional wedges beyond your iron set?",
      subtitle: "Select by loft",
      icon: Zap,
      isMultiSelect: true,
      options: [
        { value: 50, label: "50°", description: "Gap Wedge" },
        { value: 52, label: "52°", description: "Gap Wedge" },
        { value: 54, label: "54°", description: "Sand Wedge" },
        { value: 56, label: "56°", description: "Sand Wedge" },
        { value: 58, label: "58°", description: "Lob Wedge" },
        { value: 60, label: "60°", description: "Lob Wedge" },
        { value: "none", label: "None", description: "Only set wedges" },
      ],
      conditionalShow: (d: OnboardingData) => d.hasExistingClubs === true,
    },
    {
      id: "starterBudget",
      section: 2,
      sectionTitle: "YOUR EQUIPMENT",
      title: "What's your budget for a starter set?",
      subtitle: "We'll find the perfect match",
      icon: Zap,
      options: [
        { value: "under-500", label: "Under $500", description: "Great value starters" },
        { value: "500-1200", label: "$500 - $1,200", description: "Quality that grows with you" },
        { value: "over-1200", label: "$1,200+", description: "Premium investment" },
      ],
      conditionalShow: (d: OnboardingData) => d.hasExistingClubs === false,
    },
    {
      id: "sevenIronDistance",
      section: 2,
      sectionTitle: "YOUR EQUIPMENT",
      title: "How far does your 7-iron carry?",
      subtitle: "This ONE distance calibrates all 14 clubs! 🎯",
      icon: Activity,
      isSlider: true,
      sliderMin: 100,
      sliderMax: 210,
      sliderDefault: 145,
      showDistancePreview: true,
    },
    // SECTION 3: YOUR SWING
    {
      id: "ballFlight",
      section: 3,
      sectionTitle: "YOUR SWING",
      title: "What's your typical ball flight?",
      subtitle: "Pick what happens most often",
      icon: Target,
      options: [
        { value: "straight", label: "Straight", description: "Goes where I aim" },
        { value: "draw", label: "Draw", description: "Curves right to left" },
        { value: "fade", label: "Fade", description: "Curves left to right" },
        { value: "slice", label: "Slice", description: "Big curve right (need help!)" },
        { value: "hook", label: "Hook", description: "Big curve left" },
      ],
    },
    {
      id: "swingTempo",
      section: 3,
      sectionTitle: "YOUR SWING",
      title: "How's your swing tempo?",
      subtitle: "Affects shaft flex recommendations",
      icon: TrendingUp,
      options: [
        { value: "slow", label: "🐌 Slow & Smooth", description: "Like Collin Morikawa" },
        { value: "medium", label: "⚡ Medium Tempo", description: "Balanced rhythm" },
        { value: "fast", label: "🚀 Quick & Athletic", description: "Like Rory McIlroy" },
      ],
    },
    {
      id: "biggestChallenge",
      section: 3,
      sectionTitle: "YOUR SWING",
      title: "What needs the most work?",
      subtitle: "We'll focus AI recommendations here",
      icon: Target,
      options: [
        { value: "driver", label: "🏌️ Driver / Tee Shots", description: "Struggle off the tee" },
        { value: "approach", label: "🎯 Approach Shots", description: "Mid-range accuracy" },
        { value: "short-game", label: "⛳ Short Game", description: "Chipping and pitching" },
        { value: "putting", label: "🏌️‍♂️ Putting", description: "Too many 3-putts" },
      ],
    },
    {
      id: "commonMiss",
      section: 3,
      sectionTitle: "YOUR SWING",
      title: "Where do your misses usually go?",
      subtitle: "Helps recommend corrective equipment",
      icon: Activity,
      options: [
        { value: "right", label: "➡️ Right", description: "Slice or fade" },
        { value: "left", label: "⬅️ Left", description: "Hook or draw" },
        { value: "short", label: "⬇️ Short", description: "Not enough distance" },
        { value: "inconsistent", label: "🎲 Inconsistent", description: "Different every time" },
      ],
    },
    // SECTION 4: PHYSICAL
    {
      id: "physicalStats",
      section: 4,
      sectionTitle: "FINAL DETAILS",
      title: "Quick physical stats for club fitting",
      subtitle: "Height and age optimize club specifications",
      icon: User,
      isPhysicalStats: true,
    },
  ];

  const visibleSteps = steps.filter(step => {
    if (step.conditionalShow) {
      return step.conditionalShow(data);
    }
    return true;
  });

  const currentStepData = visibleSteps[currentStep];
  const isLastStep = currentStep === visibleSteps.length - 1;

  const handleOptionSelect = (stepId: string, value: any) => {
    const fieldMap: Record<string, keyof OnboardingData> = {
      experience: "playingExperience",
      frequency: "courseFrequency",
      primaryGoal: "primaryGoal",
      hasClubs: "hasExistingClubs",
      hasDriver: "hasDriver",
      hasWoods: "hasWoods",
      hasHybrids: "hasHybrids",
      selectedIronSet: "selectedIronSet",
      selectedWedgeLofts: "selectedWedgeLofts",
      selectedWedgeSet: "selectedWedgeSet",
      ballFlight: "ballFlight",
      swingTempo: "swingTempo",
      biggestChallenge: "biggestChallenge",
      commonMiss: "mostCommonMiss",
    };
    
    if (stepId === "hasClubs") {
      setData(prev => ({ ...prev, hasExistingClubs: value === "yes" }));
    } else if (stepId === "starterBudget") {
      const setId = value === "under-500" ? "good" : value === "500-1200" ? "better" : "best";
      setData(prev => ({ ...prev, starterSetId: setId }));
    } else {
      const field = fieldMap[stepId];
      if (field) {
        setData(prev => ({ ...prev, [field]: value }));
      }
    }
  };

  const toggleArrayItem = (field: keyof OnboardingData, value: string | number) => {
    setData(prev => {
      const currentArray = (prev[field] as any[]) || [];
      if (value === 'none') return { ...prev, [field]: [] };
      const withoutNone = currentArray.filter(item => item !== 'none');
      const updated = withoutNone.includes(value)
        ? withoutNone.filter(item => item !== value)
        : [...withoutNone, value];
      return { ...prev, [field]: updated };
    });
  };

  const goToStep = (direction: 'next' | 'prev') => {
    setAnimating(true);
    setTimeout(() => {
      if (direction === 'next') {
        if (isLastStep) {
          onComplete(data);
        } else {
          setCurrentStep(prev => prev + 1);
        }
      } else {
        setCurrentStep(prev => Math.max(0, prev - 1));
      }
      setAnimating(false);
    }, 150);
  };

  const nextStep = () => {
    const validators: Record<string, () => boolean> = {
      experience: () => !!data.playingExperience,
      frequency: () => !!data.courseFrequency,
      ballFlight: () => !!data.ballFlight,
      swingTempo: () => !!data.swingTempo,
    };
    
    const validator = validators[currentStepData.id];
    if (validator && !validator()) {
      return;
    }
    goToStep('next');
  };

  if (!isOpen) return null;

  const StepIcon = currentStepData.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl overflow-hidden animate-modal-in">
        {/* Hero Header */}
        <div className="relative bg-gradient-to-r from-slate-900 via-indigo-900 to-purple-900 text-white overflow-hidden flex-shrink-0">
          {/* Pattern overlay */}
          <div className="absolute inset-0 opacity-20" style={{ 
            backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }} />
          
          <div className="relative px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex-shrink-0">
                  <Sparkles size={16} className="text-amber-400 sm:w-5 sm:h-5" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-xl font-bold truncate">Complete Your Profile</h2>
                  <p className="text-indigo-200 text-xs sm:text-sm truncate">Unlock personalized recommendations</p>
                </div>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-white/10 transition-colors flex-shrink-0 ml-2"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Step Progress */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
                <span className="text-indigo-200 truncate">
                  Section {currentStepData.section} of 4: <span className="text-white font-medium">{currentStepData.sectionTitle}</span>
                </span>
                <span className="text-indigo-200 flex-shrink-0 ml-2">
                  {Math.round(((currentStep + 1) / visibleSteps.length) * 100)}%
                </span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${((currentStep + 1) / visibleSteps.length) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 overflow-y-auto p-6 transition-opacity duration-150 ${animating ? 'opacity-0' : 'opacity-100'}`}>
          {/* Question Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30 mb-4">
              <StepIcon size={28} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {currentStepData.title}
            </h3>
            <p className="text-gray-500">{currentStepData.subtitle}</p>
          </div>

          {/* Slider: Average Score */}
          {currentStepData.isSlider && currentStepData.id === "averageScore" ? (
            <div className="max-w-lg mx-auto space-y-8">
              <PremiumSlider
                value={data.averageScore || currentStepData.sliderDefault || 95}
                min={currentStepData.sliderMin || 65}
                max={currentStepData.sliderMax || 150}
                onChange={(value) => setData({ ...data, averageScore: value })}
                labels={['65 (Pro)', '95 (Avg)', '150 (New)']}
                color="purple"
              />

              {/* Persona Card */}
              <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-6 border border-indigo-100">
                <div className="text-center">
                  <div className="text-4xl mb-3">
                    {getPersonaFromScore(data.averageScore || 95).icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-1">
                    {getPersonaFromScore(data.averageScore || 95).name}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4">
                    {getPersonaFromScore(data.averageScore || 95).description}
                  </p>
                  
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {[
                      { label: 'Handicap', value: getPersonaFromScore(data.averageScore || 95).estimatedHandicap, color: 'text-emerald-600' },
                      { label: 'Swing Speed', value: `${getPersonaFromScore(data.averageScore || 95).estimatedSwingSpeed}mph`, color: 'text-blue-600' },
                      { label: 'Ball Flight', value: getPersonaFromScore(data.averageScore || 95).typicalBallFlight, color: 'text-purple-600' },
                    ].map((stat, i) => (
                      <div key={i} className="bg-white rounded-xl p-3 shadow-sm">
                        <div className={`text-lg font-bold ${stat.color} capitalize`}>{stat.value}</div>
                        <div className="text-xs text-gray-500">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : currentStepData.isSlider && currentStepData.id === "sevenIronDistance" ? (
            <div className="max-w-lg mx-auto space-y-8">
              <PremiumSlider
                value={data.sevenIronDistance || currentStepData.sliderDefault || 145}
                min={currentStepData.sliderMin || 100}
                max={currentStepData.sliderMax || 210}
                onChange={(value) => setData({ ...data, sevenIronDistance: value })}
                unit="yds"
                labels={['100 yds', '155 yds', '210 yds']}
                color="blue"
              />

              {/* Distance Preview Grid - Shows user's actual selected clubs */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
                <h4 className="font-bold text-gray-900 mb-4 text-center flex items-center justify-center gap-2">
                  <Sparkles size={16} className="text-amber-500" />
                  AI Will Estimate Your Club Distances
                </h4>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  {(() => {
                    // Build preview clubs from user's actual selections
                    const previewClubs: { name: string; multiplier: number }[] = [];
                    
                    // Add driver if selected
                    if (data.hasDriver === 'yes') {
                      previewClubs.push({ name: 'Driver', multiplier: 1.7 });
                    }
                    
                    // Add first wood if selected
                    if (data.hasWoods && data.hasWoods.length > 0) {
                      const wood = data.hasWoods[0];
                      const woodMultiplier = wood === '3W' ? 1.45 : wood === '5W' ? 1.35 : 1.25;
                      previewClubs.push({ name: wood, multiplier: woodMultiplier });
                    }
                    
                    // Add hybrid if selected
                    if (data.hasHybrids && data.hasHybrids.length > 0) {
                      const hybrid = data.hasHybrids[0];
                      const hybridMultiplier = hybrid === '3H' ? 1.25 : hybrid === '4H' ? 1.18 : 1.12;
                      previewClubs.push({ name: hybrid, multiplier: hybridMultiplier });
                    }
                    
                    // Always show 7I (the reference club)
                    previewClubs.push({ name: '7I', multiplier: 1.0 });
                    
                    // Add a wedge if selected
                    if (data.selectedWedgeLofts && data.selectedWedgeLofts.length > 0) {
                      const wedge = data.selectedWedgeLofts[0];
                      if (typeof wedge === 'number') {
                        const wedgeName = wedge <= 52 ? 'GW' : wedge <= 56 ? 'SW' : 'LW';
                        const wedgeMultiplier = wedge <= 52 ? 0.68 : wedge <= 56 ? 0.6 : 0.52;
                        previewClubs.push({ name: wedgeName, multiplier: wedgeMultiplier });
                      }
                    } else {
                      // Default to PW
                      previewClubs.push({ name: 'PW', multiplier: 0.75 });
                    }
                    
                    // If not enough clubs, add some defaults
                    if (previewClubs.length < 4) {
                      if (!previewClubs.some(c => c.name === 'Driver') && !data.hasDriver) {
                        previewClubs.unshift({ name: 'Driver', multiplier: 1.7 });
                      }
                      if (previewClubs.length < 4 && !previewClubs.some(c => c.name.includes('W'))) {
                        previewClubs.splice(1, 0, { name: '5W', multiplier: 1.35 });
                      }
                    }
                    
                    // Take first 4 clubs and sort by distance (descending)
                    return previewClubs
                      .slice(0, 4)
                      .sort((a, b) => b.multiplier - a.multiplier)
                      .map((club) => {
                        const dist = Math.round((data.sevenIronDistance || 145) * club.multiplier);
                        return (
                          <div key={club.name} className="bg-white rounded-xl p-3 text-center shadow-sm">
                            <div className="font-bold text-gray-900">{club.name}</div>
                            <div className="text-blue-600 font-bold">{dist}y</div>
                          </div>
                        );
                      });
                  })()}
                </div>
              </div>
            </div>
          ) : currentStepData.isPhysicalStats ? (
            <div className="max-w-md mx-auto space-y-8">
              {/* Height Selector */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-5 border border-indigo-100">
                <label className="block text-sm font-semibold text-gray-700 mb-4 text-center">📏 Height</label>
                <HeightPicker
                  feet={data.heightFeet || 5}
                  inches={data.heightInches || 10}
                  onFeetChange={(feet) => setData({ ...data, heightFeet: feet })}
                  onInchesChange={(inches) => setData({ ...data, heightInches: inches })}
                />
                <div className="text-center mt-3 text-sm text-indigo-600 font-medium">
                  Total: {(data.heightFeet || 5) * 12 + (data.heightInches || 10)}" ({data.heightFeet || 5}'{data.heightInches || 10}")
                </div>
              </div>

              {/* Weight */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">⚖️ Weight (lbs)</label>
                <input
                  type="number"
                  value={data.weight || ''}
                  onChange={(e) => setData({ ...data, weight: Number(e.target.value) })}
                  placeholder="Enter weight"
                  className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all text-lg font-semibold text-center"
                  min="50"
                  max="400"
                />
              </div>

              {/* Birthdate Selector */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100">
                <label className="block text-sm font-semibold text-gray-700 mb-4 text-center">🎂 Birthdate</label>
                <BirthdatePicker
                  month={data.birthMonth}
                  day={data.birthDay}
                  year={data.birthYear}
                  onMonthChange={(birthMonth) => {
                    const age = data.birthYear && birthMonth && data.birthDay 
                      ? calculateAge(data.birthYear, birthMonth, data.birthDay) : undefined;
                    setData({ ...data, birthMonth, age });
                  }}
                  onDayChange={(birthDay) => {
                    const age = data.birthYear && data.birthMonth && birthDay 
                      ? calculateAge(data.birthYear, data.birthMonth, birthDay) : undefined;
                    setData({ ...data, birthDay, age });
                  }}
                  onYearChange={(birthYear) => {
                    const age = data.birthMonth && data.birthDay 
                      ? calculateAge(birthYear, data.birthMonth, data.birthDay)
                      : new Date().getFullYear() - birthYear;
                    setData({ ...data, birthYear, age });
                  }}
                />
                {data.age && (
                  <div className="mt-4 p-3 bg-white rounded-xl text-center border border-amber-200 shadow-sm">
                    <span className="text-lg font-bold text-amber-700">🎉 Age: {data.age} years old</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Options Grid */
            <div className="space-y-3 max-w-lg mx-auto">
              {currentStepData.options?.map((option, idx) => {
                const isSelected = (() => {
                  switch (currentStepData.id) {
                    case 'experience': return data.playingExperience === option.value;
                    case 'frequency': return data.courseFrequency === option.value;
                    case 'primaryGoal': return data.primaryGoal === option.value;
                    case 'hasClubs': return data.hasExistingClubs === (option.value === 'yes');
                    case 'starterBudget': return data.starterSetId === (option.value === "under-500" ? "good" : option.value === "500-1200" ? "better" : "best");
                    case 'hasDriver': return data.hasDriver === option.value;
                    case 'hasWoods': return (data.hasWoods || []).includes(option.value as string);
                    case 'hasHybrids': return (data.hasHybrids || []).includes(option.value as string);
                    case 'selectedIronSet': return data.selectedIronSet === option.value;
                    case 'selectedWedgeLofts': return (data.selectedWedgeLofts || []).includes(option.value as number);
                    case 'ballFlight': return data.ballFlight === option.value;
                    case 'swingTempo': return data.swingTempo === option.value;
                    case 'biggestChallenge': return data.biggestChallenge === option.value;
                    case 'commonMiss': return data.mostCommonMiss === option.value;
                    default: return false;
                  }
                })();

                return (
                  <OptionCard
                    key={option.value}
                    label={option.label}
                    description={option.description}
                    selected={isSelected}
                    isMultiSelect={currentStepData.isMultiSelect}
                    onClick={() => {
                      if (currentStepData.isMultiSelect) {
                        toggleArrayItem(currentStepData.id as keyof OnboardingData, option.value);
                      } else {
                        handleOptionSelect(currentStepData.id, option.value);
                      }
                    }}
                    delay={idx * 50}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={() => goToStep('prev')}
            disabled={currentStep === 0}
            className={`
              flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium
              transition-all duration-200
              ${currentStep === 0
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }
            `}
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onSkip}
              className="text-gray-500 hover:text-gray-700 font-medium text-sm px-4 py-2"
            >
              Skip
            </button>
            <button
              onClick={nextStep}
              className="
                flex items-center gap-2 
                bg-gradient-to-r from-indigo-600 to-purple-600 text-white 
                px-6 py-2.5 rounded-xl font-semibold
                shadow-lg shadow-indigo-500/30
                hover:shadow-xl hover:shadow-indigo-500/40
                hover:-translate-y-0.5
                transition-all duration-200
              "
            >
              {isLastStep ? 'Complete Setup' : 'Continue'}
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
